import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import CategoryPagination from './CategoryPagination';

import { SelectedFilters } from '@invideoio/web-shared/types/Filters';
import { isUserLoggedIn } from '@utils/common.utils';
import { getEditorRedirectionUrl } from '@utils/template-category/redirect-to-editor';
import { RedirectContext } from '@store/store';
import { AUTH_TYPE, baseHost } from '@constants/homepage';
import {
  TemplateSearchQuery,
  LIHPTemplate,
} from '@invideoio/web-shared/types/Templates';
import { TEMPLATE_LIMIT } from '@constants/templates';

const TemplatesGrid = dynamic(
  () =>
    /* webpackChunkName: "TemplatesGrid" */
    import('@invideoio/web-shared/Modules/IVTemplatesGrid'),
);

const TemplatesPreview = dynamic(
  () =>
    /* webpackChunkName: "TemplatesPreview" */
    import('@invideoio/web-shared/Modules/IVTemplatePreview'),
);

const TemplatePreviewHeader = dynamic(
  () =>
    /* webpackChunkName: "TemplatePreviewHeader" */
    import('@components/category-templates/TemplatePreviewHeader'),
);

const BackToTop = dynamic(
  () =>
    /* webpackChunkName: "BackToTop" */
    import('@components/BackToTop'),
);
interface CategoryTemplatesProps {
  templateList: any;
  filters: TemplateSearchQuery;
  totalCount: number;
  currerntPage?: number;
  platform: string;
  moreLink?: string;
}

function CategoryTemplates(props: CategoryTemplatesProps) {
  const {
    templateList,
    filters,
    totalCount,
    currerntPage = 1,
    platform,
    moreLink,
  } = props;
  const router = useRouter();

  const [selectedTemplate, setSelectedTemplate] = useState<LIHPTemplate>();

  const [isBackToTopVisible, setIsBackToTopVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { handleClick } = useContext(RedirectContext);

  const scrollToTop = () => {
    containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const defaultQueryFilter = useMemo(() => {
    let queryFilters: SelectedFilters = {
      c: [],
      d: [],
      i: [],
      offset:
        currerntPage > 1 ? currerntPage * TEMPLATE_LIMIT - TEMPLATE_LIMIT : 0,
    };

    if (filters.categories_info) {
      queryFilters.c = filters.categories_info.map(cat => cat.category_value);
    }

    if (filters.dimensions) {
      queryFilters.d = filters.dimensions;
    }

    if (filters.industries) {
      queryFilters.i = filters.industries;
    }

    if (filters.search_term) {
      queryFilters.searchTerm = filters.search_term;
    }

    return queryFilters;
  }, [filters, currerntPage]);

  const handleScroll = () => {
    if (
      containerRef?.current?.scrollTop &&
      containerRef?.current?.scrollTop > 2000
    ) {
      setIsBackToTopVisible(true);
    } else {
      setIsBackToTopVisible(false);
    }
  };

  const [templateState, setTemplateState] = useState({
    hasNoMore: false,
    isFetching: false,
  });

  const totalPages = Math.ceil(totalCount / TEMPLATE_LIMIT);

  function openEditor(selectedDimension: any) {
    if (selectedTemplate) {
      // Absolute studio URL: a relative /editor resolves on invideo.io, where
      // the worker now sends /editor to the gen3 signup instead of studio.
      const url = getEditorRedirectionUrl(
        '',
        selectedDimension,
        selectedTemplate.id.toString(),
        true,
        'all',
        'story',
        'oneClick',
        true,
      );

      window.open(baseHost + url, '_self');
    }
  }

  const onUseTemplate = (template: any, selectedDimension: any) => {
    if (!isUserLoggedIn()) {
      const url = getEditorRedirectionUrl(
        '',
        selectedDimension,
        template.id.toString(),
        true,
        'all',
        'story',
        'oneClick',
        true,
      );

      handleClick(baseHost + url, { tab: AUTH_TYPE.SIGNUP });
    } else {
      openEditor(selectedDimension);
    }
  };

  useEffect(() => {
    if (router.query) {
      const isCMSPreview = router.query['cmsPreview'];

      if (isCMSPreview && isCMSPreview === 'true') {
        setTemplateState(currentState => ({
          ...currentState,
          hasNoMore: true,
        }));
      }
    }
  }, [router]);

  useEffect(() => {
    setTemplateState(currentState => ({
      ...currentState,
      templates: templateList,
    }));
  }, [templateList]);

  const closeModal = () => {
    setSelectedTemplate(undefined);
  };
  const templatePreviewModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      // Escape keyCode is 27
      // Close modal on esc key press
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);

  const onSelectTemplate = (template: LIHPTemplate) => {
    if (platform !== 'desktop') {
      const url = '/workflow/marketing-templates/' + template.id;
      if (isUserLoggedIn()) {
        window.open(url, '_self');
      } else {
        handleClick(url, { tab: AUTH_TYPE.SIGNUP });
      }
    } else {
      setSelectedTemplate(template);
    }
  };

  const onSeeMoreClicked = () => {
    window.open(moreLink || '/templates', '_self');
  };

  const onRelatedTemplateSelect = (template?: LIHPTemplate) => {
    setSelectedTemplate(template);
  };

  return (
    <div
      className={ivclass(
        'iv-w-full',
        'lg:iv-py-25 xl:iv-py-25 xl:iv-pl-25 lg:iv-pl-25 md:iv-py-15 md:iv-px-5 sm:iv-px-5 sm:iv-py-15',
      )}
      ref={containerRef}
      onScroll={handleScroll}
    >
      <div className={ivclass('')}>
        <TemplatesGrid
          defaultQueryFilters={defaultQueryFilter}
          leftPanelWidth={0}
          onTemplateSelect={onSelectTemplate}
          options={{
            templateGridWrapperClass: '',
            templateGridContainerClass: 'iv-px-[0px]',
          }}
        />
      </div>

      <div
        className={ivclass(
          'iv-flex iv-justify-between iv-items-center',
          'iv-py-15',
        )}
      >
        <div
          className={ivclass('iv-text-body-m iv-font-medium iv-text-grey-60')}
        >
          Page {currerntPage} of {totalPages}
        </div>
        <CategoryPagination
          totalPages={totalPages}
          currentPage={currerntPage}
        />
      </div>

      <div
        className={ivclass(
          'iv-fixed  iv-left-0 iv-right-0 iv-bottom-0 iv-z-20',
          'iv-overflow-auto',
          'iv-bg-grey-0',
          'iv-ease-in-out iv-duration-500',
          selectedTemplate
            ? 'iv-opacity-1 iv-visible iv-top-[56px]'
            : 'iv-opacity-0 iv-invisible iv-top-[56px]',
          '!iv-border-t-[1px] iv-border-grey-7 iv-border-solid iv-border-x-0 iv-border-y-0',
          !selectedTemplate && '!iv-border-t-[0px]',
        )}
        ref={templatePreviewModalRef}
      >
        {selectedTemplate && (
          <div className="iv-px-20">
            <TemplatePreviewHeader
              template={selectedTemplate}
              isModal={true}
              closeModal={closeModal}
            />
          </div>
        )}
        {selectedTemplate && (
          <div className={ivclass('iv-flex iv-justify-center')}>
            <TemplatesPreview
              previewData={selectedTemplate}
              options={{
                showRelatedTemplates: true,
                wrapperClass: '!iv-w-full',
              }}
              onChooseTemplate={onUseTemplate}
              relatedTemplateProps={{
                onSeeMoreClicked,
                onRelatedTemplateSelect,
              }}
            />
          </div>
        )}
      </div>

      {isBackToTopVisible && <BackToTop onClick={scrollToTop} />}
    </div>
  );
}

export default CategoryTemplates;
